import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import breakpoints, { forDesktop } from '@westwing/ui-kit/breakPoints';
import { GaTracking } from '@westwing/appshell_types';
import { useClubContext } from './hooks/ClubContext';
import { ClubLink } from './types/clubLinksTypes';
import { POKey, useHeaderContext } from 'Components/HeaderContext';

enum NavigationItemId {
    CAMPAIGN = 'campaign',
    UPCOMING = 'upcoming',
    INVITE = 'invite',
}

const navigationBarLinks: NavigationBarItem[] = [
    {
        id: NavigationItemId.CAMPAIGN,
        translationKey: 'OwwHeader_Current Sales',
        pathName: ClubLink.CAMPAIGN,
        testId: 'oww-header-navigation-bar-campaign',
        onClick: trackNonInteractiveGenericEvent =>
            trackNonInteractiveGenericEvent('Navigation', { action: 'Current Campaigns Click' }),
    },
    {
        id: NavigationItemId.UPCOMING,
        translationKey: 'OwwHeader_Upcoming Sales',
        pathName: ClubLink.CAMPAIGN_UPCOMING,
        testId: 'oww-header-navigation-bar-upcoming',
        onClick: trackNonInteractiveGenericEvent =>
            trackNonInteractiveGenericEvent('Navigation', { action: 'Upcoming Campaigns Click' }),
    },
    {
        id: NavigationItemId.INVITE,
        translationKey: 'OwwHeader_Invite a friend',
        pathName: ClubLink.CUSTOMER_ACCOUNT_CREATE_INVITATION,
        testId: 'oww-header-navigation-bar-invite',
        noOverlay: true,
        onClick: (trackNonInteractiveGenericEvent, pageType) =>
            trackNonInteractiveGenericEvent('Referral Program', {
                action: 'Regerral Navigation – Click',
                label: pageType,
            }),
    },
];

const navigationBarLinksIds = navigationBarLinks.map(item => item.id);

const navBarLinksParams = navigationBarLinksIds.reduce((links, link) => {
    // eslint-disable-next-line no-param-reassign
    links[link] = '';
    return links;
}, {});

const StyledNavigationContainer = styled.div`
    height: 48px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.charcoal20};
    background: ${({ theme }) => theme.colors.white};
    padding-left: 40px;
    padding-right: 40px;
    display: none;

    ${forDesktop`
        display: block;
    `}
`;

const StyledNavItemsList = styled.ul`
    max-width: ${breakpoints.breakpointWideScreens};
    margin: 0 auto;
    display: flex;
    align-items: center;
    gap: 52px;
    height: 100%;

    ${({ theme }) =>
        navigationBarLinksIds.map(
            id => `
        &[data-selected-id="${id}"] [data-item-id="${id}"] {
            color: ${theme.colors.turquoise100};

            a {
                &:hover,
                &:focus {
                    color: ${theme.colors.turquoise50};
                }
            }

            &::before {
                opacity: 1;
            };
        }
    `
        )}
`;

const StyledLink = styled.a`
    text-decoration: none;
    color: inherit;
    position: relative;
    font-size: 14px;
    line-height: 20px;

    &:hover,
    &:focus {
        color: ${({ theme }) => theme.colors.charcoal50};
    }
`;

const StyledNavItem = styled.li`
    position: relative;
    display: flex;
    align-items: center;
    height: 100%;
    color: ${({ theme }) => theme.colors.charcoal80};
    font-family: ${({ theme }) => theme.fonts.fontBrandonMedium};
    -webkit-font-smoothing: antialiased;

    &::before {
        opacity: 0;
        content: '';
        bottom: 0;
        left: 0;
        right: 0;
        width: 100%;
        height: 1px;
        background: ${({ theme }) => theme.colors.turquoise100};
        position: absolute;
    }
`;

const NavigationBar = ({ selectedNavigationItem, noOverlayForCOPNav, isLoggedIn }: NavigationBarProps): JSX.Element => {
    const { t } = useHeaderContext();
    const { links } = useClubContext();
    const [queryParams, setQueryParams] = useState(navBarLinksParams);
    const [unblockCOP, setUnblockCop] = React.useState(false);

    const {
        trackingHelpers: {
            ga: { trackNonInteractiveGenericEvent },
            useCurrentPageType,
        },
    } = useHeaderContext();
    const { serverSide } = useClubContext();

    const { pageType } = useCurrentPageType();

    React.useEffect(() => {
        setUnblockCop(noOverlayForCOPNav);
    }, [noOverlayForCOPNav]);

    useEffect(() => {
        if (isLoggedIn) {
            return;
        }

        const params = [NavigationItemId.CAMPAIGN, NavigationItemId.UPCOMING].reduce((items, item) => {
            // eslint-disable-next-line no-param-reassign
            items[item] = '?pr=fetch';
            return items;
        }, {});

        setQueryParams(existingParams => ({ ...existingParams, ...params }));
    }, [isLoggedIn]);

    const parameterizedLink = (navItem: NavigationBarItem) => {
        const link = links[navItem.pathName].path;
        const param = link.endsWith('/') ? `${queryParams[navItem.id]}` : `/${queryParams[navItem.id]}`;
        return link + param;
    };

    return (
        <StyledNavigationContainer>
            <StyledNavItemsList
                role="navigation"
                data-selected-id={selectedNavigationItem}
                data-testid="oww-header-navigation-bar"
            >
                {navigationBarLinks.map(navItem => (
                    <StyledNavItem
                        data-no-overlay={
                            navItem.noOverlay || (!serverSide && navItem.id === 'campaign' && unblockCOP)
                                ? true
                                : undefined
                        }
                        data-item-id={navItem.id}
                        key={links[navItem.pathName].path}
                        data-testid={navItem.testId}
                        onClick={() => navItem.onClick(trackNonInteractiveGenericEvent, `${pageType}`)}
                    >
                        <StyledLink href={parameterizedLink(navItem)}>{t(navItem.translationKey)}</StyledLink>
                    </StyledNavItem>
                ))}
            </StyledNavItemsList>
        </StyledNavigationContainer>
    );
};

export default NavigationBar;

export interface NavigationBarProps {
    selectedNavigationItem: string;
    noOverlayForCOPNav: boolean;
    isLoggedIn: boolean;
}

export interface NavigationBarItem {
    id: NavigationItemId;
    translationKey: POKey;
    pathName: ClubLink;
    testId: string;
    /** This will avoid login overlay from appearing when customer clicks on it while logged out */
    noOverlay?: boolean;
    onClick: (trackNonInteractiveGenericEvent: GaTracking['trackGenericEvent'], pageType: string) => unknown;
}
